import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';

import { sendGTMEvent } from '@/fo/api/gtm';
import CloseIcon from '@/fo/icons/base/icons/close24.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { staticMediaUrl } from '@/fo/util/image-loader';

import { FilterLabelList, SelectedFilters } from '../../types';
import { LabelListFilter } from '../FilterOptions/LabelListFilter/LabelListFilter';

import { styles } from './PickUpCare.styles';

export interface PickUpCareProps {
  pickUpCareFilters: FilterLabelList[];
  selectedFilters: SelectedFilters;
  productsCount: number;
  onTagClick: (slug: string, value: string, checked: boolean) => void;
  onReset: () => void;
  onShowProducts: () => void;
  className?: string;
  onOpenChange?: (open: boolean) => void;
  isLoading?: boolean;
  isOpenModal?: boolean;
  gtmEventCategory?: string;
}

export function PickUpCare({
  pickUpCareFilters = [],
  selectedFilters = {},
  productsCount = 0,
  onTagClick,
  onReset,
  onShowProducts,
  className,
  onOpenChange,
  isLoading = false,
  isOpenModal = false,
  gtmEventCategory
}: PickUpCareProps) {
  const slots = styles();
  const t = useTranslations();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpenModal && isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal]);

  const openDrawer = (open: boolean) => {
    if (open) {
      setIsOpen(true);
      sendGTMEvent({
        event: 'care_select',
        eventCategory: gtmEventCategory || ''
      } as Parameters<typeof sendGTMEvent>[0]);
    }
    onOpenChange?.(open);
  };

  const hasSelectedCareOptions = useMemo(() => {
    return Object.entries(selectedFilters).some(
      ([filterSlug, selectedValues]) => {
        const filterExists = pickUpCareFilters.some(
          (filter) => filter.isCareSelection && filter.slug === filterSlug
        );
        return filterExists && selectedValues.length > 0;
      }
    );
  }, [selectedFilters, pickUpCareFilters]);

  const trigger = (
    <UiKitLabelBase
      className={slots.careButton({ className, hasSelectedCareOptions })}
      variant="default"
    >
      <div className={slots.careElementContent()}>
        <Image
          src={staticMediaUrl('product/care.png')}
          alt={t('productCatalog.pickUpCare')}
          className={slots.careElementImage()}
          width={38}
          height={38}
        />
        <span>{t('productCatalog.pickUpCare')}</span>
        {hasSelectedCareOptions && (
          <UiKitButtonBase
            color="ghost"
            rounded="full"
            className={slots.clearButton()}
            childrenClassName={slots.clearButtonIconWrapper()}
            onClick={(e) => {
              e.stopPropagation();
              onReset();
            }}
          >
            <CloseIcon
              width={16}
              height={16}
            />
          </UiKitButtonBase>
        )}
      </div>
    </UiKitLabelBase>
  );

  if (pickUpCareFilters?.length === 0) return null;

  return (
    <UiKitDrawerBase
      title={t('productCatalog.pickUpCare')}
      direction="right"
      trigger={trigger}
      contentClassName={slots.drawer()}
      withoutMobile
      onOpenChange={openDrawer}
      open={isOpen}
    >
      <div className={slots.base()}>
        <div className={slots.header()}>
          <div className={slots.titleWrapper()}>
            <h2 className={slots.title()}>{t('productCatalog.pickUpCare')}</h2>
            {hasSelectedCareOptions && (
              <UiKitButtonBase
                color="ghost"
                onClick={onReset}
                className={slots.resetButton()}
              >
                {t('reset')}
              </UiKitButtonBase>
            )}
          </div>
          <p className={slots.description()}>
            {t('productCatalog.pickUpCareDescription')}
          </p>
        </div>
        <div className={slots.filters()}>
          {pickUpCareFilters?.map((filter) => {
            const options =
              hasSelectedCareOptions && filter.options
                ? filter.options.filter((opt) => {
                    const selectedValues = selectedFilters[filter.slug];
                    const isSelected =
                      Array.isArray(selectedValues) &&
                      selectedValues.includes(opt.value as never);

                    return isSelected || (opt.count ?? 1) > 0;
                  })
                : filter.options;

            if (options?.length === 0) return null;

            return (
              <div
                key={`${filter.label}-${filter.slug}`}
                className={slots.filter()}
              >
                <h3 className={slots.question()}>{filter.label}</h3>
                <LabelListFilter
                  selected={selectedFilters[filter.slug] || []}
                  onChange={(value, checked) =>
                    onTagClick(filter.slug, value as string, Boolean(checked))
                  }
                  {...filter}
                  options={options}
                />
              </div>
            );
          })}
        </div>
        <div className={slots.footer()}>
          <UiKitButtonBase
            className={slots.showProductsButton()}
            onClick={() => {
              sendGTMEvent({
                event: 'apply_filter',
                eventAction: 'care_filter',
                eventCategory: gtmEventCategory || ''
              } as Parameters<typeof sendGTMEvent>[0]);
              onShowProducts();
            }}
            disabled={productsCount === 0}
            loading={isLoading}
          >
            {t('productCatalog.showProductByCount', {
              count: productsCount
            })}
          </UiKitButtonBase>
        </div>
      </div>
    </UiKitDrawerBase>
  );
}
